import {
  Box,
  Table,
  Flex,
  Text,
} from "@chakra-ui/react";
import { colors } from "../../theme";
import AppButton from "../AppButton";
import { ChevronLeftIcon, ChevronRightIcon } from "../../utils/svg";

/**
 * AppTable - Generic table wrapper component
 * 
 * @param {array} headers - Array of column titles
 * @param {array} data - Array of row objects
 * @param {function} renderRow - Function for custom row rendering
 * @param {object} pagination - Pagination config { page, totalPages, onPageChange }
 * @param {boolean} isLoading - Show loading state
 * @param {string} emptyMessage - Message when no data
 */
const AppTable = ({
  headers = [],
  data = [],
  renderRow,
  pagination,
  isLoading = false,
  emptyMessage = "No data available",
  ...rest
}) => {
  const { page = 1, totalPages = 1, onPageChange } = pagination || {};

  return (
    <Box
      bg="white"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="sm"
      {...rest}
    >
      <Box overflowX="auto">
        <Table.Root size="md">
          <Table.Header>
            <Table.Row bg={colors.background}>
              {headers.map((header, index) => (
                <Table.ColumnHeader
                  key={index}
                  py={3}
                  px={4}
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.textSecondary}
                  textTransform="uppercase"
                  letterSpacing="wide"
                  borderBottom="1px solid"
                  borderColor={colors.disabled}
                >
                  {header}
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell
                  colSpan={headers.length}
                  textAlign="center"
                  py={8}
                >
                  <Text color={colors.textSecondary}>Loading...</Text>
                </Table.Cell>
              </Table.Row>
            ) : data.length === 0 ? (
              <Table.Row>
                <Table.Cell
                  colSpan={headers.length}
                  textAlign="center"
                  py={8}
                >
                  <Text color={colors.textSecondary}>{emptyMessage}</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              data.map((row, index) => renderRow(row, index))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Pagination Footer */}
      {pagination && totalPages > 1 && (
        <Flex
          justify="space-between"
          align="center"
          px={4}
          py={3}
          borderTop="1px solid"
          borderColor={colors.disabled}
          bg={colors.background}
        >
          <Text fontSize="sm" color={colors.textSecondary}>
            Page {page} of {totalPages}
          </Text>
          <Flex gap={2}>
            <AppButton
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
              leftIcon={<ChevronLeftIcon boxSize={4} />}
            >
              Previous
            </AppButton>
            <AppButton
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange?.(page + 1)}
              rightIcon={<ChevronRightIcon boxSize={4} />}
            >
              Next
            </AppButton>
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

// Table Cell component for consistent styling
export const TableCell = ({ children, ...props }) => (
  <Table.Cell
    py={3}
    px={4}
    fontSize="sm"
    color={colors.text}
    borderBottom="1px solid"
    borderColor={colors.disabled}
    {...props}
  >
    {children}
  </Table.Cell>
);

// Table Row component for consistent styling
export const TableRow = ({ children, onClick, ...props }) => (
  <Table.Row
    _hover={{ bg: colors.background }}
    cursor={onClick ? "pointer" : "default"}
    onClick={onClick}
    transition="background 0.2s"
    {...props}
  >
    {children}
  </Table.Row>
);

export default AppTable;
